// apps/web/src/components/parent/ApprovalItem.tsx
// Single checklist item card for parent review. Shows photos, AI feedback, timeline,
// and approve/return actions when item is pending_parent.

import { useState } from 'react';
import type { ChecklistItem, ChecklistStatus } from '@homework-app/shared';
import { ApprovalTimeline } from './ApprovalTimeline';

interface Props {
  item: ChecklistItem;
  onApprove: (itemId: string) => void;
  onReturn: (itemId: string, comment: string) => void;
  compact?: boolean;
}

const STATUS_LABELS: Record<ChecklistStatus, string> = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  pending_ai: 'AI Reviewing',
  pending_parent: 'Needs Approval',
  approved: 'Approved',
  returned: 'Returned'
};

const STATUS_COLORS: Record<ChecklistStatus, string> = {
  not_started: '#6b7280',
  in_progress: '#3b82f6',
  pending_ai: '#f59e0b',
  pending_parent: '#8b5cf6',
  approved: '#10b981',
  returned: '#ef4444'
};

export function ApprovalItem({ item, onApprove, onReturn, compact = false }: Props) {
  const [returnComment, setReturnComment] = useState('');
  const [showReturn, setShowReturn] = useState(false);
  const [showPhotos, setShowPhotos] = useState(!compact);

  const beforePhotos = (item.photos || []).filter(p => p.phase === 'before');
  const afterPhotos = (item.photos || []).filter(p => p.phase === 'after');
  const hasPhotos = beforePhotos.length > 0 || afterPhotos.length > 0;

  const handleReturn = () => {
    if (!returnComment.trim()) return;
    onReturn(item.id, returnComment);
    setReturnComment('');
    setShowReturn(false);
  };

  const statusColor = STATUS_COLORS[item.status] || '#6b7280';

  return (
    <div style={{
      borderLeft: `4px solid ${statusColor}`,
      borderRadius: '0 8px 8px 0',
      padding: '0.875rem',
      marginBottom: '0.75rem',
      background: compact ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.05)',
      border: '1px solid rgba(255,255,255,0.08)',
      borderLeftColor: statusColor
    }}>
      {/* Header row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
        <div>
          <div style={{ fontWeight: 600, color: '#f0f0f5', fontSize: '0.95rem' }}>{item.title}</div>
          <span style={{
            fontSize: '0.7rem',
            color: '#8b8b9e',
            background: 'rgba(255,255,255,0.07)',
            padding: '2px 6px',
            borderRadius: 4,
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}>
            {item.item_type}
          </span>
        </div>
        <span style={{
          fontSize: '0.75rem',
          padding: '3px 8px',
          borderRadius: 12,
          background: `${statusColor}22`,
          color: statusColor,
          border: `1px solid ${statusColor}44`,
          whiteSpace: 'nowrap'
        }}>
          {STATUS_LABELS[item.status]}
        </span>
      </div>

      {/* AI verification feedback */}
      {item.verification && (
        <div style={{
          margin: '0.5rem 0',
          padding: '0.5rem 0.75rem',
          borderRadius: 6,
          background: item.verification.passed ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
          border: `1px solid ${item.verification.passed ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`,
          fontSize: '0.8rem',
          color: '#d1d5db'
        }}>
          <strong style={{ color: item.verification.passed ? '#10b981' : '#ef4444' }}>
            MARVIN:
          </strong>{' '}
          {item.verification.feedback}
          <span style={{ color: '#6b7280', marginLeft: '0.5rem' }}>
            ({Math.round(item.verification.confidence * 100)}% confident)
          </span>
        </div>
      )}

      {/* Returned comment from parent */}
      {item.parent_comment && item.status === 'returned' && (
        <div style={{
          margin: '0.5rem 0',
          padding: '0.5rem 0.75rem',
          borderRadius: 6,
          background: 'rgba(239,68,68,0.08)',
          border: '1px solid rgba(239,68,68,0.2)',
          fontSize: '0.8rem',
          color: '#fca5a5'
        }}>
          <strong>Returned:</strong> {item.parent_comment}
        </div>
      )}

      {/* Photos */}
      {hasPhotos && showPhotos && (
        <div style={{ display: 'flex', gap: '1rem', margin: '0.5rem 0', flexWrap: 'wrap' }}>
          {beforePhotos.length > 0 && (
            <div>
              <div style={{ fontSize: '0.7rem', color: '#8b8b9e', marginBottom: '0.25rem', textTransform: 'uppercase' }}>Before</div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {beforePhotos.map(p => (
                  <img
                    key={p.id}
                    src={p.url}
                    alt="Before"
                    style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 6, border: '1px solid rgba(255,255,255,0.1)' }}
                  />
                ))}
              </div>
            </div>
          )}
          {afterPhotos.length > 0 && (
            <div>
              <div style={{ fontSize: '0.7rem', color: '#8b8b9e', marginBottom: '0.25rem', textTransform: 'uppercase' }}>After</div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {afterPhotos.map(p => (
                  <img
                    key={p.id}
                    src={p.url}
                    alt="After"
                    style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 6, border: '1px solid rgba(255,255,255,0.1)' }}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {hasPhotos && compact && (
        <button
          onClick={() => setShowPhotos(prev => !prev)}
          style={{
            fontSize: '0.75rem',
            color: '#a78bfa',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '0.25rem 0',
            fontFamily: 'inherit'
          }}
        >
          {showPhotos ? 'Hide photos' : `Show photos (${beforePhotos.length + afterPhotos.length})`}
        </button>
      )}

      {/* Timeline */}
      <ApprovalTimeline item={item} />

      {/* Action buttons — only for pending_parent items */}
      {item.status === 'pending_parent' && (
        <div style={{ marginTop: '0.75rem' }}>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={() => onApprove(item.id)}
              style={{
                padding: '0.4rem 1rem',
                background: 'rgba(16,185,129,0.2)',
                color: '#10b981',
                border: '1px solid rgba(16,185,129,0.4)',
                borderRadius: 6,
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: '0.85rem',
                fontFamily: 'inherit'
              }}
            >
              Approve
            </button>
            <button
              onClick={() => setShowReturn(prev => !prev)}
              style={{
                padding: '0.4rem 1rem',
                background: 'rgba(239,68,68,0.1)',
                color: '#f87171',
                border: '1px solid rgba(239,68,68,0.3)',
                borderRadius: 6,
                cursor: 'pointer',
                fontSize: '0.85rem',
                fontFamily: 'inherit'
              }}
            >
              Return
            </button>
          </div>

          {showReturn && (
            <div style={{ marginTop: '0.75rem' }}>
              <textarea
                value={returnComment}
                onChange={e => setReturnComment(e.target.value)}
                placeholder="What needs to be fixed? (required)"
                rows={2}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: 6,
                  border: '1px solid rgba(255,255,255,0.1)',
                  background: 'rgba(255,255,255,0.07)',
                  color: '#f0f0f5',
                  fontFamily: 'inherit',
                  fontSize: '0.85rem',
                  resize: 'vertical',
                  boxSizing: 'border-box'
                }}
              />
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                <button
                  onClick={handleReturn}
                  disabled={!returnComment.trim()}
                  style={{
                    padding: '0.4rem 0.875rem',
                    background: returnComment.trim() ? 'rgba(239,68,68,0.2)' : 'rgba(255,255,255,0.05)',
                    color: returnComment.trim() ? '#f87171' : '#6b7280',
                    border: `1px solid ${returnComment.trim() ? 'rgba(239,68,68,0.4)' : 'rgba(255,255,255,0.08)'}`,
                    borderRadius: 6,
                    cursor: returnComment.trim() ? 'pointer' : 'not-allowed',
                    fontSize: '0.85rem',
                    fontFamily: 'inherit'
                  }}
                >
                  Send Back
                </button>
                <button
                  onClick={() => { setShowReturn(false); setReturnComment(''); }}
                  style={{
                    padding: '0.4rem 0.875rem',
                    background: 'rgba(255,255,255,0.05)',
                    color: '#8b8b9e',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    fontFamily: 'inherit'
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
